import { useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import FilterDailyComparison, { DailyComparisonData } from '@/components/filters/FilterDailyComparison';
import AppCard from '@/components/appcard/AppCard';
import AppSectionLoader from '@/components/AppSectionLoader';
import { getMenuData } from '@/core/data/menuData';
import { useNotifications } from '@/core/hooks/useNotifications';
import { apiClient } from '@core/api';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { FilterRef } from '@features/statistics/types.ts';

interface ThresholdData {
    name: string;
    value: number;
}

interface FillLevelData {
    intervalStart: string;
    max: number;
    average: number;
    min: number;
}

interface SeriesData {
    name: string;
    data: Array<{ x: string; y: number }>;
}

interface FilterArgs {
    shopId: string;
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
    interval: number;
}

const TrolleyShelterDailyComparison: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [heading1, setHeading1] = useState(t('fillLevel'));
    const [heading2, setHeading2] = useState(t('fillLevel'));

    const [series1, setSeries1] = useState<SeriesData[]>([]);
    const [series2, setSeries2] = useState<SeriesData[]>([]);

    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);

    const chartConfig1 = useMemo(
        () => ({
            syncChartId: 'TROLLEY',
            chartId: 'TROLLEY-1',
            strokeCurve: 'straight' as const,
            strokeDashArray: [0, 0, 0, 10, 10, 10] as number[],
            yAxisMin: 0,
            yAxisForceNiceScale: true,
            xAxisType: 'datetime' as const,
            tooltipShared: true
        }),
        []
    );

    const chartConfig2 = useMemo(
        () => ({
            syncChartId: 'TROLLEY',
            chartId: 'TROLLEY-2',
            strokeCurve: 'straight' as const,
            strokeDashArray: [0, 0, 0, 10, 10, 10] as number[],
            yAxisMin: 0,
            yAxisForceNiceScale: true,
            xAxisType: 'datetime' as const,
            tooltipShared: true
        }),
        []
    );

    const loadChartData = useCallback(
        async (args: FilterArgs): Promise<SeriesData[]> => {
            try {
                const [shelterThresholds, shelterFillLevels] = await Promise.all([
                    apiClient.get<ThresholdData[]>(`trolleyshelter/${args.deviceId}/${args.moduleId}/threshold`),
                    apiClient.get<FillLevelData[]>(
                        `statistics/shelterfilllevel/${args.deviceId}/${args.moduleId}/${args.start}/${args.end}/${args.interval}`
                    )
                ]);

                const series: SeriesData[] = [
                    { name: 'Max', data: [] },
                    { name: t('average'), data: [] },
                    { name: 'Min', data: [] }
                ];

                const sortedThresholds = [...shelterThresholds.data].sort((a, b) => b.value - a.value);
                sortedThresholds.forEach((th) => {
                    series.push({ name: th.name, data: [] });
                });

                shelterFillLevels.data.forEach((element) => {
                    const intervalStart = DateTime.fromISO(element.intervalStart).toString();
                    series[0].data.push({ x: intervalStart, y: element.max });
                    series[1].data.push({ x: intervalStart, y: element.average });
                    series[2].data.push({ x: intervalStart, y: element.min });
                    for (let i = 0; i < sortedThresholds.length; i += 1) {
                        series[3 + i].data.push({ x: intervalStart, y: sortedThresholds[i].value });
                    }
                });

                return series;
            } catch (error) {
                notifyErrorResponse(error);
                return [];
            }
        },
        [notifyErrorResponse, t]
    );

    const loadChart1 = useCallback(
        async (args: FilterArgs) => {
            const data = await loadChartData(args);
            setSeries1(data);
        },
        [loadChartData]
    );

    const loadChart2 = useCallback(
        async (args: FilterArgs) => {
            const data = await loadChartData(args);
            setSeries2(data);
        },
        [loadChartData]
    );

    const loadData = useCallback(
        async (args: DailyComparisonData) => {
            const newHeading1 = `${t('fillLevel')} ${args.moduleId1}`;
            const newHeading2 = `${t('fillLevel')} ${args.moduleId2}`;

            // only update headings when changed
            setHeading1((prev) => (prev === newHeading1 ? prev : newHeading1));
            setHeading2((prev) => (prev === newHeading2 ? prev : newHeading2));

            const args1 = {
                shopId: args.shopId1,
                deviceId: args.deviceId1,
                moduleId: args.moduleId1,
                start: args.day1Start,
                end: args.day1End,
                interval: args.interval
            };

            const args2 = {
                shopId: args.shopId2,
                deviceId: args.deviceId2,
                moduleId: args.moduleId2,
                start: args.day2Start,
                end: args.day2End,
                interval: args.interval
            };

            setLoading(true);
            await Promise.all([loadChart1(args1), loadChart2(args2)]);
            setLastUpdate(DateTime.now());
            setLoading(false);
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload, loading);

    return (
        <div>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-cast zmdi-hc-rotate-180" style={{ fontSize: '16px' }} />}
                title={`${t('trolleyShelter')} ${t('dailyComparison')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/rfid/dailycomparison' }}
                lastUpdate={lastUpdate}
                t={t}
            />
            <div className="container grid-list-xl container--fluid no-padding-top">
                <FilterDailyComparison ref={filterRef} forType="trolleyShelter" onLoadData={loadData} />
                <div className="row">
                    <div className="col-md-6">
                        <AppCard
                            heading={heading1}
                            icon="cast"
                            iconClass="rotate-180"
                            colClasses="col-spacing"
                            customClasses="no-padding-bottom"
                            fullBlock={false}
                            footer={false}
                        >
                            <WcnApexchart
                                type="line"
                                series={series1}
                                height={410}
                                syncChartId={chartConfig1.syncChartId}
                                chartId={chartConfig1.chartId}
                                strokeCurve={chartConfig1.strokeCurve}
                                strokeDashArray={chartConfig1.strokeDashArray}
                                yAxisMin={chartConfig1.yAxisMin}
                                yAxisForceNiceScale={chartConfig1.yAxisForceNiceScale}
                                tooltipShared={chartConfig1.tooltipShared}
                                xAxisType={chartConfig1.xAxisType}
                            />
                        </AppCard>
                    </div>
                    <div className="col-md-6">
                        <AppCard
                            heading={heading2}
                            icon="cast"
                            iconClass="rotate-180"
                            colClasses="col-spacing"
                            customClasses="no-padding-bottom"
                            fullBlock={false}
                            footer={false}
                        >
                            <WcnApexchart
                                type="line"
                                series={series2}
                                height={410}
                                syncChartId={chartConfig2.syncChartId}
                                chartId={chartConfig2.chartId}
                                strokeCurve={chartConfig2.strokeCurve}
                                strokeDashArray={chartConfig2.strokeDashArray}
                                yAxisMin={chartConfig2.yAxisMin}
                                yAxisForceNiceScale={chartConfig2.yAxisForceNiceScale}
                                tooltipShared={chartConfig2.tooltipShared}
                                xAxisType={chartConfig2.xAxisType}
                            />
                        </AppCard>
                    </div>

                    <AppSectionLoader status={loading} />
                </div>
            </div>
        </div>
    );
};

export default TrolleyShelterDailyComparison;
